import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, ActivityIndicator } from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { Input, Button } from '../design-system';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { apiService } from '../../services/api';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useFlowPermissions } from '../../hooks/useFlowPermissions';

interface FlowVersionItem {
  _id: string;
  versionNumber: number;
  versionName?: string;
  isPublished: boolean;
  savedSnapshot?: boolean;
  publishedAt?: string;
  createdAt: string;
  createdByName?: string;
}

interface FlowVersionHistoryProps {
  visible: boolean;
  onClose: () => void;
  flowId: string;
  /** Called after a version is restored into the draft or rolled back to published — the
   * caller should reload the flow (both change what the builder's current draft/publish
   * state is) and, for restore, navigate into the now-updated editable draft. */
  onChanged: () => void;
}

/** Version browser: lists every save and every publish of a flow (most recent first), lets the business
 * rename a version, restore an old version into the current editable draft ("View" — per
 * product decision, this intentionally loads it as normal editable content rather than a
 * separate read-only viewer, so inspecting an old version and turning it into a new one is
 * the same action), or roll back by re-publishing an older version directly. */
export const FlowVersionHistory: React.FC<FlowVersionHistoryProps> = ({ visible, onClose, flowId, onChanged }) => {
  const { canEdit, canAdmin } = useFlowPermissions();
  const styles = useThemedStyles(getStyles);
  const [versions, setVersions] = useState<FlowVersionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyVersionId, setBusyVersionId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getFlowVersions(flowId);
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setVersions(res.data);
      }
    } catch (error) {
      console.error('Error loading flow versions:', error);
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    if (visible) load();
  }, [visible, load]);

  const handleRestore = async (versionId: string) => {
    try {
      setBusyVersionId(versionId);
      const res = await apiService.restoreFlowVersionToDraft(flowId, versionId);
      if (res.statusCode === 1000) {
        onChanged();
        onClose();
      }
    } catch (error) {
      console.error('Error restoring flow version:', error);
    } finally {
      setBusyVersionId(null);
    }
  };

  const handleRollback = async (versionId: string) => {
    try {
      setBusyVersionId(versionId);
      const res = await apiService.publishFlowVersion(flowId, versionId);
      if (res.statusCode === 1000) {
        onChanged();
        await load();
      }
    } catch (error) {
      console.error('Error rolling back flow version:', error);
    } finally {
      setBusyVersionId(null);
    }
  };

  const startRename = (version: FlowVersionItem) => {
    setRenamingId(version._id);
    setRenameDraft(version.versionName || `Version ${version.versionNumber}`);
  };

  const commitRename = async () => {
    if (!renamingId || !renameDraft.trim()) {
      setRenamingId(null);
      return;
    }
    try {
      const res = await apiService.renameFlowVersion(flowId, renamingId, renameDraft.trim());
      if (res.statusCode === 1000) {
        setVersions((prev) => prev.map((v) => (v._id === renamingId ? { ...v, versionName: renameDraft.trim() } : v)));
      }
    } catch (error) {
      console.error('Error renaming flow version:', error);
    } finally {
      setRenamingId(null);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.content}>
          <View style={styles.header}>
            <Text style={styles.title}>Version History</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
          </View>
          {loading ? (
            <View style={styles.emptyContainer}><ActivityIndicator size="large" color={colors.primary} /></View>
          ) : (
            <FlatList
              data={versions}
              keyExtractor={(v) => v._id}
              renderItem={({ item }) => {
                const isBusy = busyVersionId === item._id;
                const isRenaming = renamingId === item._id;
                return (
                  <View style={styles.row}>
                    <View style={styles.rowTop}>
                      {isRenaming ? (
                        <View style={{ flex: 1 }}>
                          <Input
                            value={renameDraft}
                            onChangeText={setRenameDraft}
                            size="sm"
                            autoFocus
                            onBlur={commitRename}
                            onSubmitEditing={commitRename}
                          />
                        </View>
                      ) : (
                        <TouchableOpacity style={{ flex: 1 }} onPress={() => startRename(item)}>
                          <View style={styles.nameRow}>
                            <Text style={styles.versionName} numberOfLines={1}>{item.versionName || (item.savedSnapshot ? 'Saved' : `Version ${item.versionNumber}`)}</Text>
                            <Ionicons name="pencil-outline" size={12} color={colors.textLight} />
                          </View>
                        </TouchableOpacity>
                      )}
                      {item.isPublished && (
                        <View style={styles.publishedBadge}>
                          <Text style={styles.publishedBadgeText}>Live</Text>
                        </View>
                      )}
                      {!item.savedSnapshot && !item.isPublished && (
                        <View style={styles.savedBadge}>
                          <Text style={styles.savedBadgeText}>Published before</Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.meta}>
                      {item.savedSnapshot ? 'Save' : `v${item.versionNumber}`} · {new Date(item.publishedAt || item.createdAt).toLocaleString()}{item.createdByName ? ` · ${item.createdByName}` : ''}
                    </Text>
                    <View style={styles.actions}>
                      {canEdit && <TouchableOpacity style={styles.actionButton} onPress={() => handleRestore(item._id)} disabled={isBusy}>
                        {isBusy ? <ActivityIndicator size="small" color={colors.primary} /> : (
                          <>
                            <Ionicons name="eye-outline" size={14} color={colors.primary} />
                            <Text style={styles.actionButtonText}>View / Edit</Text>
                          </>
                        )}
                      </TouchableOpacity>}
                      {!item.isPublished && canAdmin && (
                        <TouchableOpacity style={styles.actionButton} onPress={() => handleRollback(item._id)} disabled={isBusy}>
                          <Ionicons name="arrow-undo-outline" size={14} color={colors.primary} />
                          <Text style={styles.actionButtonText}>Publish This Version</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                );
              }}
              ListEmptyComponent={<View style={styles.emptyContainer}><Text style={styles.emptyText}>No versions yet. Every save and every publish shows here.</Text></View>}
            />
          )}
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'flex-end' },
  content: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.lg,
    borderTopRightRadius: designTokens.borderRadius.lg,
    maxHeight: '80%',
    paddingBottom: spacing.lg,
  },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.lg, borderBottomWidth: 1, borderBottomColor: colors.border },
  title: { fontSize: typography.fontSize.xl, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.bold },
  row: { paddingHorizontal: spacing.lg, paddingVertical: spacing.md, borderBottomWidth: 1, borderBottomColor: `${String(colors.border)}40` },
  rowTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  versionName: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  publishedBadge: { backgroundColor: '#DCFCE7', paddingHorizontal: spacing.xs, paddingVertical: 2, borderRadius: designTokens.borderRadius.sm },
  savedBadge: { backgroundColor: colors.background.secondary, paddingHorizontal: spacing.xs, paddingVertical: 2, borderRadius: designTokens.borderRadius.sm },
  savedBadgeText: { fontSize: 10, color: colors.textLight, fontFamily: typography.fontFamily.semibold },
  publishedBadgeText: { fontSize: 10, color: '#15803D', fontFamily: typography.fontFamily.semibold },
  meta: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.primary, marginTop: 2 },
  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.sm },
  actionButton: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs / 2 },
  actionButtonText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  emptyContainer: { padding: spacing.xl, alignItems: 'center', justifyContent: 'center' },
  emptyText: { fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.primary, textAlign: 'center' },
});
